import type { FC } from 'react';

import { _isNil, _isNotBlank } from '@/littledash';
import type { ISODate, ISODateTime } from '@/model/Common.model';
import Banner from '@/components/UI/Banner/Banner';
import { renderStudyClosureDate } from '@/components/Studies/Show/StudyClosureDate.utils';

interface StudyCompletedBannerProps {
  completedOn?: ISODate | null;
  archivedAt?: ISODateTime | null;
  timezone?: string;
  completerName?: string | null;
  testId?: string;
}

/**
 * Displays a banner when a study has been marked complete, showing the user-picked
 * completion calendar date and optional completer attribution from StudyInfo.
 */
const StudyCompletedBanner: FC<StudyCompletedBannerProps> = ({
  completedOn,
  archivedAt,
  timezone,
  completerName,
  testId = 'study-completed-banner',
}) => {
  if (_isNil(archivedAt)) {
    return null;
  }

  const completedDate = renderStudyClosureDate(completedOn, archivedAt, timezone);

  return (
    <Banner info dismiss={false} className="mb4" testId={testId}>
      <div data-test-component="StudyCompletedBanner" data-test-element="content">
        <h3 className="lh-title f5 ma0 blue fw5" data-testid="study-completed-banner__title">
          Study completed on {completedDate}
        </h3>
        {_isNotBlank(completerName) && (
          <p className="f6 lh-copy mt1 mb0" data-testid="study-completed-banner__completer">
            Marked as complete by {completerName}
          </p>
        )}
      </div>
    </Banner>
  );
};

export default StudyCompletedBanner;
